import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../utils/api';
import { useCart } from '../../context/CartContext';
import toast from 'react-hot-toast';

const CATS = [
  { key:'all',  label:{fr:'Tous les produits', en:'All products'}, dot:'#94a3b8' },
  { key:'med',  label:{fr:'Médicaments',       en:'Medicines'},    dot:'#185FA5' },
  { key:'dis',  label:{fr:'Dispositifs',        en:'Devices'},      dot:'#0F6E56' },
  { key:'cons', label:{fr:'Consommables',       en:'Consumables'},  dot:'#854F0B' },
  { key:'lab',  label:{fr:'Laboratoire',        en:'Laboratory'},   dot:'#7F77DD' },
];

const CAT_LABELS = {
  med:{fr:'Médicament',en:'Medicine'},
  dis:{fr:'Dispositif',en:'Device'},
  cons:{fr:'Consommable',en:'Consumable'},
  lab:{fr:'Laboratoire',en:'Laboratory'}
};

function WishBtn({ active, onClick }) {
  return (
    <button onClick={e => { e.stopPropagation(); onClick(); }} style={{
      width:34, height:34, border:'0.5px solid #e2e8f0', borderRadius:8,
      background:'#fff', cursor:'pointer', display:'flex', alignItems:'center',
      justifyContent:'center', flexShrink:0
    }}>
      <svg viewBox="0 0 24 24" style={{ width:14, height:14, fill:active?'#E24B4A':'none', stroke:active?'#E24B4A':'#94a3b8', strokeWidth:2 }}>
        <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
      </svg>
    </button>
  );
}

function ProductCardGrid({ product, lang, onAdd, wished, onWish }) {
  const name = lang==='fr' ? product.nameFr : product.nameEn;
  const catLabel = CAT_LABELS[product.category]?.[lang] || product.category;
  return (
    <div style={{ background:'#fff', border:'0.5px solid #e2e8f0', borderRadius:12, overflow:'hidden', transition:'border-color 0.15s', display:'flex', flexDirection:'column' }}
      onMouseEnter={e=>e.currentTarget.style.borderColor='#378ADD'}
      onMouseLeave={e=>e.currentTarget.style.borderColor='#e2e8f0'}>
      <div style={{ height:130, overflow:'hidden', background:'#f1f5f9', flexShrink:0, position:'relative' }}>
        {product.imageUrl
          ? <img src={product.imageUrl} alt={name} loading="lazy" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} onError={e=>e.target.style.display='none'}/>
          : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:30 }}>💊</div>
        }
        {product.badge && <span style={{ position:'absolute', top:8, left:8, background:'#1D9E75', color:'#fff', fontSize:10, fontWeight:600, padding:'2px 8px', borderRadius:10 }}>{lang==='fr'?'Nouveau':'New'}</span>}
      </div>
      <div style={{ padding:12, display:'flex', flexDirection:'column', flex:1 }}>
        <div style={{ fontSize:10, color:'#94a3b8', marginBottom:3, fontFamily:'monospace' }}>{product.ref}</div>
        <div style={{ fontSize:13, fontWeight:500, color:'#1a2535', marginBottom:7, lineHeight:1.35, minHeight:38, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{name}</div>
        <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:8, flexWrap:'wrap' }}>
          <span style={{ fontSize:10, padding:'2px 7px', borderRadius:6, fontWeight:500, background:'#E1F5EE', color:'#085041' }}>{lang==='fr'?'En stock':'In stock'}</span>
          <span style={{ fontSize:10, padding:'2px 7px', borderRadius:6, fontWeight:500, background:'#f1f5f9', color:'#64748b' }}>{catLabel}</span>
        </div>
        <div style={{ fontSize:16, fontWeight:600, color:'#185FA5', marginBottom:10 }}>{product.price} MAD</div>
        <div style={{ marginTop:'auto', display:'flex', gap:6 }}>
          <button onClick={e => { e.stopPropagation(); onAdd(product); }} style={{ flex:1, background:'#0B4F8A', color:'#fff', border:'none', padding:8, borderRadius:8, fontSize:12, fontWeight:500, cursor:'pointer' }}
            onMouseEnter={e=>e.target.style.background='#185FA5'}
            onMouseLeave={e=>e.target.style.background='#0B4F8A'}>
            {lang==='fr'?'+ Ajouter':'+ Add'}
          </button>
          <WishBtn active={wished} onClick={() => onWish(product._id)}/>
        </div>
      </div>
    </div>
  );
}

function ProductCardList({ product, lang, onAdd, wished, onWish }) {
  const name = lang==='fr' ? product.nameFr : product.nameEn;
  const catLabel = CAT_LABELS[product.category]?.[lang] || product.category;
  return (
    <div style={{ background:'#fff', border:'0.5px solid #e2e8f0', borderRadius:12, overflow:'hidden', display:'grid', gridTemplateColumns:'110px 1fr auto', transition:'border-color 0.15s' }}
      onMouseEnter={e=>e.currentTarget.style.borderColor='#378ADD'}
      onMouseLeave={e=>e.currentTarget.style.borderColor='#e2e8f0'}>
      <div style={{ overflow:'hidden', background:'#f1f5f9', minHeight:90 }}>
        {product.imageUrl
          ? <img src={product.imageUrl} alt={name} loading="lazy" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} onError={e=>e.target.style.display='none'}/>
          : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:26 }}>💊</div>
        }
      </div>
      <div style={{ padding:'12px 14px' }}>
        <div style={{ fontSize:10, color:'#94a3b8', marginBottom:3, fontFamily:'monospace' }}>{product.ref}</div>
        <div style={{ fontSize:14, fontWeight:500, color:'#1a2535', marginBottom:6, lineHeight:1.3 }}>{name}</div>
        <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
          <span style={{ fontSize:10, padding:'2px 7px', borderRadius:6, background:'#E1F5EE', color:'#085041', fontWeight:500 }}>{lang==='fr'?'En stock':'In stock'}</span>
          {product.badge && <span style={{ fontSize:10, padding:'2px 7px', borderRadius:6, background:'#E1F5EE', color:'#085041', fontWeight:500 }}>{lang==='fr'?'Nouveau':'New'}</span>}
          <span style={{ fontSize:10, padding:'2px 7px', borderRadius:6, background:'#f1f5f9', color:'#64748b', fontWeight:500 }}>{catLabel}</span>
        </div>
      </div>
      <div style={{ padding:'12px 14px 12px 0', display:'flex', flexDirection:'column', alignItems:'flex-end', justifyContent:'space-between', minWidth:120 }}>
        <div style={{ fontSize:17, fontWeight:600, color:'#185FA5' }}>{product.price} MAD</div>
        <div style={{ display:'flex', gap:6 }}>
          <button onClick={() => onAdd(product)} style={{ background:'#0B4F8A', color:'#fff', border:'none', padding:'7px 14px', borderRadius:8, fontSize:12, fontWeight:500, cursor:'pointer' }}>
            {lang==='fr'?'+ Ajouter':'+ Add'}
          </button>
          <WishBtn active={wished} onClick={() => onWish(product._id)}/>
        </div>
      </div>
    </div>
  );
}

export default function Catalogue({ lang }) {
  const { addItem } = useCart();
  const fr = lang === 'fr';
  const [cat, setCat]             = useState('all');
  const [search, setSearch]       = useState('');
  const [sort, setSort]           = useState('def');
  const [view, setView]           = useState('grid');
  const [wishlist, setWishlist]   = useState(new Set());
  const [priceMin, setPriceMin]   = useState('');
  const [priceMax, setPriceMax]   = useState('');
  const [stockOnly, setStockOnly] = useState(false);
  const [newOnly, setNewOnly]     = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile]   = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => api.get('/products', { params: { limit: 100 } }).then(r => r.data)
  });

  const allProducts = data?.data || [];

  const catCounts = {};
  CATS.forEach(c => {
    catCounts[c.key] = c.key==='all' ? allProducts.length : allProducts.filter(p=>p.category===c.key).length;
  });

  let filtered = allProducts.filter(p => {
    if (cat !== 'all' && p.category !== cat) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!p.nameFr.toLowerCase().includes(q) && !p.nameEn.toLowerCase().includes(q) && !p.ref.toLowerCase().includes(q)) return false;
    }
    if (newOnly && !p.badge) return false;
    const price = parseFloat(p.price);
    if (priceMin && price < parseFloat(priceMin)) return false;
    if (priceMax && price > parseFloat(priceMax)) return false;
    return true;
  });

  if (sort==='price-asc')  filtered.sort((a,b) => parseFloat(a.price)-parseFloat(b.price));
  if (sort==='price-desc') filtered.sort((a,b) => parseFloat(b.price)-parseFloat(a.price));
  if (sort==='name')       filtered.sort((a,b) => a.nameFr.localeCompare(b.nameFr));
  if (sort==='new')        filtered.sort((a,b) => (b.badge?1:0)-(a.badge?1:0));

  const handleAdd = (product) => {
    addItem(product);
    toast.success((fr ? product.nameFr : product.nameEn) + (fr ? ' ajouté !' : ' added!'));
  };

  const toggleWish = (id) => {
    setWishlist(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Titre dynamique selon la catégorie sélectionnée
  const catTitles = {
    all:  fr ? 'Catalogue Dispositifs Médicaux — LOMED MEDICARE Maroc' : 'Medical Devices Catalogue — LOMED MEDICARE Morocco',
    med:  fr ? 'Médicaments certifiés — LOMED MEDICARE Maroc' : 'Certified Medicines — LOMED MEDICARE Morocco',
    dis:  fr ? 'Dispositifs médicaux certifiés CE — LOMED MEDICARE' : 'CE Certified Medical Devices — LOMED MEDICARE',
    cons: fr ? 'Consommables médicaux — LOMED MEDICARE Maroc' : 'Medical Consumables — LOMED MEDICARE Morocco',
    lab:  fr ? 'Matériel laboratoire médical — LOMED MEDICARE' : 'Medical Laboratory Equipment — LOMED MEDICARE',
  };

  const catDescs = {
    all:  fr ? '500+ dispositifs médicaux certifiés CE disponibles au Maroc. Médicaments, dispositifs, consommables et matériel de laboratoire. Livraison 48h.' : '500+ CE certified medical devices available in Morocco. Medicines, devices, consumables and laboratory equipment. 48h delivery.',
    med:  fr ? 'Médicaments certifiés pour cliniques et hôpitaux au Maroc. Commande en ligne, livraison 48h, traçabilité garantie.' : 'Certified medicines for clinics and hospitals in Morocco. Online order, 48h delivery, guaranteed traceability.',
    dis:  fr ? 'Dispositifs médicaux certifiés CE : monitoring, chirurgie, cardiologie. Livraison 48h aux établissements de santé marocains.' : 'CE certified medical devices: monitoring, surgery, cardiology. 48h delivery to Moroccan healthcare facilities.',
    cons: fr ? 'Consommables médicaux stériles certifiés CE. Sutures, pansements, gants, seringues. Livraison 48h au Maroc.' : 'CE certified sterile medical consumables. Sutures, dressings, gloves, syringes. 48h delivery in Morocco.',
    lab:  fr ? 'Matériel et réactifs de laboratoire médical au Maroc. Tubes, analyseurs, réactifs. Livraison rapide.' : 'Medical laboratory equipment and reagents in Morocco. Tubes, analyzers, reagents. Fast delivery.',
  };

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: fr ? 'Catalogue dispositifs médicaux LOMED MEDICARE' : 'LOMED MEDICARE medical devices catalogue',
    description: fr ? 'Catalogue de dispositifs médicaux certifiés CE disponibles au Maroc' : 'CE certified medical devices catalogue available in Morocco',
    numberOfItems: allProducts.length,
    url: 'https://lomedmedicare.com/catalogue',
  };

  const inp = {
    border:'0.5px solid #e2e8f0', borderRadius:6, padding:'6px 8px',
    fontSize:12, background:'#f8fafc', color:'#1a2535', outline:'none', fontFamily:'inherit'
  };

  const Sidebar = () => (
    <div style={{ background:'#fff', border:'0.5px solid #e2e8f0', borderRadius:12, overflow:'hidden' }}>
      <div style={{ padding:'14px 14px 10px' }}>
        <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.8px', color:'#64748b', marginBottom:10 }}>{fr?'Catégories':'Categories'}</div>
        {CATS.map(c => (
          <div key={c.key} onClick={() => { setCat(c.key); setSidebarOpen(false); }} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 10px', borderRadius:8, cursor:'pointer', marginBottom:2, background: cat===c.key ? '#E6F1FB' : 'transparent' }}
            onMouseEnter={e=>{ if(cat!==c.key) e.currentTarget.style.background='#f8fafc'; }}
            onMouseLeave={e=>{ if(cat!==c.key) e.currentTarget.style.background='transparent'; }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:cat===c.key?'#0C447C':'#1a2535', fontWeight:cat===c.key?600:400 }}>
              <span style={{ width:8, height:8, borderRadius:2, background:c.dot, flexShrink:0 }}/>
              {c.label[lang]||c.label.fr}
            </div>
            <span style={{ fontSize:11, padding:'2px 7px', borderRadius:10, background:cat===c.key?'#B5D4F4':'#f1f5f9', color:cat===c.key?'#0C447C':'#64748b' }}>{catCounts[c.key]||0}</span>
          </div>
        ))}
      </div>
      <div style={{ height:'0.5px', background:'#e2e8f0' }}/>
      <div style={{ padding:'12px 14px 10px' }}>
        <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.8px', color:'#64748b', marginBottom:10 }}>{fr?'Filtres':'Filters'}</div>
        <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer', padding:'5px 0' }}>
          <input type="checkbox" checked={stockOnly} onChange={e=>setStockOnly(e.target.checked)}/>{fr?'En stock uniquement':'In stock only'}
        </label>
        <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer', padding:'5px 0' }}>
          <input type="checkbox" checked={newOnly} onChange={e=>setNewOnly(e.target.checked)}/>{fr?'Nouveautés':'New products'}
        </label>
      </div>
      <div style={{ height:'0.5px', background:'#e2e8f0' }}/>
      <div style={{ padding:'12px 14px 14px' }}>
        <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.8px', color:'#64748b', marginBottom:10 }}>{fr?'Prix (MAD)':'Price (MAD)'}</div>
        <div style={{ display:'flex', gap:6, alignItems:'center' }}>
          <input type="number" placeholder="Min" value={priceMin} onChange={e=>setPriceMin(e.target.value)} style={{ ...inp, width:70 }}/>
          <span style={{ color:'#94a3b8', fontSize:12 }}>—</span>
          <input type="number" placeholder="Max" value={priceMax} onChange={e=>setPriceMax(e.target.value)} style={{ ...inp, width:70 }}/>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth:960, margin:'0 auto', padding:'1rem' }}>

      {/* ── SEO HEAD ── */}
      
        <title>{catTitles[cat]}</title>
        <meta name="description" content={catDescs[cat]} />
        <meta name="keywords" content={fr
          ? 'dispositifs médicaux Maroc, catalogue médical, matériel médical clinique, consommables médicaux, livraison 48h Maroc'
          : 'medical devices Morocco, medical catalogue, clinical medical equipment, medical consumables, 48h delivery Morocco'} />
        <link rel="canonical" href="https://lomedmedicare.com/catalogue" />
        <meta property="og:title" content={catTitles[cat]} />
        <meta property="og:description" content={catDescs[cat]} />
        <meta property="og:url" content="https://lomedmedicare.com/catalogue" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      

      {/* Header */}
      <div style={{ marginBottom:'1rem' }}>
        <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'1px', color:'#1D9E75', marginBottom:4 }}>
          {fr?'Catalogue LOMED MEDICARE':'LOMED MEDICARE Catalogue'}
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
          <h1 style={{ fontSize:20, fontWeight:700, flex:1, margin:0 }}>{fr?'Dispositifs médicaux certifiés CE':'CE certified medical devices'}</h1>
          <div style={{ position:'relative', flex:1, minWidth:200 }}>
            <svg viewBox="0 0 24 24" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', width:14, height:14, fill:'none', stroke:'#94a3b8', strokeWidth:2 }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input type="text" placeholder={fr?'Rechercher...':'Search...'} value={search} onChange={e=>setSearch(e.target.value)} style={{ ...inp, width:'100%', padding:'9px 12px 9px 32px' }}/>
          </div>
        </div>
      </div>

      {/* Mobile filter toggle */}
      {isMobile && (
        <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ width:'100%', background:'#fff', border:'0.5px solid #e2e8f0', borderRadius:8, padding:'10px 14px', fontSize:13, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
          <span style={{ display:'flex', alignItems:'center', gap:8 }}>
            <svg viewBox="0 0 24 24" style={{ width:15, height:15, fill:'none', stroke:'currentColor', strokeWidth:2 }}><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="14" y2="12"/><line x1="4" y1="18" x2="10" y2="18"/></svg>
            {fr?'Filtres & catégories':'Filters & categories'}
          </span>
          <span style={{ fontSize:12 }}>{sidebarOpen?'▲':'▼'}</span>
        </button>
      )}
      {isMobile && sidebarOpen && <div style={{ marginBottom:12 }}><Sidebar/></div>}

      <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'190px 1fr', gap:16, alignItems:'start' }}>
        {!isMobile && <Sidebar/>}
        <div>
          {/* Toolbar */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12, gap:10, flexWrap:'wrap' }}>
            <div style={{ fontSize:13, color:'#64748b' }}>{filtered.length} {fr?'produit(s)':'product(s)'}</div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <select value={sort} onChange={e=>setSort(e.target.value)} style={{ ...inp, cursor:'pointer' }}>
                <option value="def">{fr?'Tri par défaut':'Default sort'}</option>
                <option value="price-asc">{fr?'Prix croissant':'Price: low to high'}</option>
                <option value="price-desc">{fr?'Prix décroissant':'Price: high to low'}</option>
                <option value="name">{fr?'Nom A–Z':'Name A–Z'}</option>
                <option value="new">{fr?'Nouveautés':'New first'}</option>
              </select>
              <div style={{ display:'flex', border:'0.5px solid #e2e8f0', borderRadius:8, overflow:'hidden' }}>
                {['grid','list'].map(v => (
                  <button key={v} onClick={()=>setView(v)} style={{ background:view===v?'#f1f5f9':'#fff', border:'none', padding:'6px 9px', cursor:'pointer', color:view===v?'#1a2535':'#94a3b8', display:'flex', alignItems:'center' }}>
                    {v==='grid'
                      ? <svg viewBox="0 0 24 24" style={{ width:14, height:14, fill:'none', stroke:'currentColor', strokeWidth:2 }}><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                      : <svg viewBox="0 0 24 24" style={{ width:14, height:14, fill:'none', stroke:'currentColor', strokeWidth:2 }}><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                    }
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Products */}
          {isLoading ? (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px, 1fr))', gap:12 }}>
              {[...Array(6)].map((_,i) => <div key={i} style={{ background:'#f1f5f9', borderRadius:12, height:280 }}/>)}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign:'center', padding:'3rem', color:'#94a3b8', background:'#fff', borderRadius:12, border:'0.5px solid #e2e8f0' }}>
              {fr?'Aucun produit trouvé':'No products found'}
            </div>
          ) : view==='grid' ? (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px, 1fr))', gap:12 }}>
              {filtered.map(p => <ProductCardGrid key={p._id} product={p} lang={lang} onAdd={handleAdd} wished={wishlist.has(p._id)} onWish={toggleWish}/>)}
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {filtered.map(p => <ProductCardList key={p._id} product={p} lang={lang} onAdd={handleAdd} wished={wishlist.has(p._id)} onWish={toggleWish}/>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

